import React, { useState, useMemo, UIEvent, useEffect, useRef } from 'react';
import * as Y from 'yjs';
import { Awareness } from 'y-protocols/awareness';
import { CELL_WIDTH, CELL_HEIGHT } from '../constants';

// --- UI Constants ---
const HEADER_WIDTH = 45;
const HEADER_HEIGHT = 24;

export const getColName = (colIndex: number) => {
    let name = '';
    let c = colIndex;
    while (c >= 0) {
        name = String.fromCharCode(65 + (c % 26)) + name;
        c = Math.floor(c / 26) - 1;
    }
    return name;
};

export const colNameToIndex = (name: string) => {
    let index = 0;
    for (let i = 0; i < name.length; i++) {
        index = index * 26 + (name.charCodeAt(i) - 64);
    }
    return index - 1;
};

export const parseCellId = (id: string) => {
    const match = id.match(/^([A-Za-z]+)(\d+)$/);
    if (!match) return { c: -1, r: -1 };
    return { c: colNameToIndex(match[1].toUpperCase()), r: parseInt(match[2], 10) - 1 };
};

export interface CellFormat {
  bold?: boolean;
  italic?: boolean;
  color?: string;
  bg?: string;
  fontFamily?: string;
  borders?: { top?: string, bottom?: string, left?: string, right?: string };
  numberFormat?: 'currency' | 'percentage' | 'plain';
  currencyCode?: string;
}

export interface SelectionRange {
    startR: number;
    startC: number;
    endR: number;
    endC: number;
}

interface GridProps {
    ydoc: Y.Doc;
    cellsMap: Y.Map<string>; 
    formatsMap: Y.Map<any>;
    colWidthsMap: Y.Map<number>;
    rowHeightsMap: Y.Map<number>;
    conditionalRules: Y.Array<any>;
    activeSheetId: string;
    computedValues: Record<string, any>;
    awareness?: Awareness | null;
    activeCellId: string | null;
    onCellSelect: (cellId: string | null) => void;
    selection: SelectionRange | null;
    setSelection: React.Dispatch<React.SetStateAction<SelectionRange | null>>;
    onInsertRow: (r: number, count: number) => void;
    onInsertCol: (c: number, count: number) => void;
    onClearFormat: (r?: number, c?: number) => void;
    hiddenRows: Set<number>;
    groupedRows: Set<number>;
    groupedCols: Set<number>;
    onUngroupRow: (r: number) => void;
    onUngroupCol: (c: number) => void;
    zoom: number;
    freezeRow: number;
    freezeCol: number;
    totalRows: number;
    totalCols: number;
    isReadOnly?: boolean; 
}

const VirtualisedGrid: React.FC<GridProps> = ({ 
    ydoc, 
    cellsMap, 
    formatsMap, 
    colWidthsMap,
    rowHeightsMap,
    conditionalRules, 
    activeSheetId, 
    computedValues, 
    activeCellId,
    onCellSelect,
    selection,
    setSelection,
    onInsertRow,
    onInsertCol,
    onClearFormat,
    hiddenRows,
    groupedRows,
    groupedCols,
    onUngroupRow,
    onUngroupCol,
    zoom,
    freezeRow,
    freezeCol,
    totalRows,
    totalCols,
    isReadOnly = false
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [scrollPosition, setScrollPosition] = useState({ top: 0, left: 0 });
    const [version, setVersion] = useState(0);
    
    const [activeDomCell, setActiveDomCell] = useState<string | null>(null);
    const [contextMenu, setContextMenu] = useState<{ x: number, y: number, r: number, c: number } | null>(null);

    // Resizing States
    const [resizing, setResizing] = useState<{ type: 'col' | 'row', index: number, startPos: number, startSize: number } | null>(null);
    const [tempSizes, setTempSizes] = useState<{ type: 'col' | 'row', index: number, size: number } | null>(null);

    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) setContainerSize({ width: containerRef.current.clientWidth, height: containerRef.current.clientHeight });
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {
        const handleUpdate = () => setVersion((v) => v + 1);
        cellsMap.observe(handleUpdate);
        formatsMap.observe(handleUpdate);
        conditionalRules.observe(handleUpdate);
        colWidthsMap.observe(handleUpdate);
        rowHeightsMap.observe(handleUpdate);
        return () => {
            cellsMap.unobserve(handleUpdate);
            formatsMap.unobserve(handleUpdate);
            conditionalRules.unobserve(handleUpdate);
            colWidthsMap.unobserve(handleUpdate);
            rowHeightsMap.unobserve(handleUpdate);
        };
    }, [cellsMap, formatsMap, conditionalRules, colWidthsMap, rowHeightsMap]);

    useEffect(() => {
        if (!resizing) return;
        const handleMouseMove = (e: MouseEvent) => {
            if (isReadOnly) return;
            const delta = resizing.type === 'col' ? (e.clientX - resizing.startPos) / zoom : (e.clientY - resizing.startPos) / zoom;
            const newSize = Math.max(20, resizing.startSize + delta);
            setTempSizes({ type: resizing.type, index: resizing.index, size: newSize });
        };
        const handleMouseUp = () => {
            if (tempSizes) {
                if (tempSizes.type === 'col') colWidthsMap.set(tempSizes.index.toString(), tempSizes.size);
                else rowHeightsMap.set(tempSizes.index.toString(), tempSizes.size);
            }
            setResizing(null);
            setTempSizes(null);
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [resizing, tempSizes, zoom, colWidthsMap, rowHeightsMap, isReadOnly]);

    const visibleRowIndices = useMemo(() => {
        const indices = [];
        for (let i = 0; i < totalRows; i++) {
            if (!hiddenRows.has(i)) indices.push(i);
        }
        return indices;
    }, [hiddenRows, totalRows]);

    const visibleColIndices = useMemo(() => {
        const indices = [];
        for (let i = 0; i < totalCols; i++) {
            if (!groupedCols.has(i)) indices.push(i);
        }
        return indices;
    }, [groupedCols, totalCols]);

    const rowToVisual = useMemo(() => {
        const map = new Map<number, number>();
        visibleRowIndices.forEach((r, i) => map.set(r, i));
        return map;
    }, [visibleRowIndices]);

    const colToVisual = useMemo(() => {
        const map = new Map<number, number>();
        visibleColIndices.forEach((c, i) => map.set(c, i));
        return map;
    }, [visibleColIndices]);

    // Compute cumulative prefix sums for rows
    const { rowOffsets, totalHeight } = useMemo(() => {
        const offsets = new Float64Array(visibleRowIndices.length + 1);
        let currentTop = 0;
        for (let i = 0; i < visibleRowIndices.length; i++) {
            offsets[i] = currentTop;
            const r = visibleRowIndices[i];
            let height = rowHeightsMap.get(r.toString()) || CELL_HEIGHT;
            if (tempSizes?.type === 'row' && tempSizes.index === r) height = tempSizes.size;
            currentTop += height;
        }
        offsets[visibleRowIndices.length] = currentTop;
        return { rowOffsets: offsets, totalHeight: currentTop };
    }, [visibleRowIndices, version, tempSizes]);

    // Compute cumulative prefix sums for columns
    const { colOffsets, totalWidth } = useMemo(() => {
        const offsets = new Float64Array(visibleColIndices.length + 1);
        let currentLeft = 0;
        for (let i = 0; i < visibleColIndices.length; i++) {
            offsets[i] = currentLeft;
            const c = visibleColIndices[i];
            let width = colWidthsMap.get(c.toString()) || CELL_WIDTH;
            if (tempSizes?.type === 'col' && tempSizes.index === c) width = tempSizes.size;
            currentLeft += width;
        }
        offsets[visibleColIndices.length] = currentLeft;
        return { colOffsets: offsets, totalWidth: currentLeft };
    }, [visibleColIndices, version, tempSizes]);

    useEffect(() => {
        if (activeCellId && !activeDomCell && containerRef.current) {
            const coords = parseCellId(activeCellId);
            if (coords.r !== -1 && coords.c !== -1) {
                const visualRow = rowToVisual.get(coords.r);
                const visualCol = colToVisual.get(coords.c);

                if (visualRow !== undefined && visualCol !== undefined) {
                    const targetTop = rowOffsets[visualRow] * zoom;
                    const targetLeft = colOffsets[visualCol] * zoom;
                    
                    const isVisible = 
                        targetTop >= scrollPosition.top && targetTop <= scrollPosition.top + containerSize.height - ((rowHeightsMap.get(coords.r.toString()) || CELL_HEIGHT) * zoom) &&
                        targetLeft >= scrollPosition.left && targetLeft <= scrollPosition.left + containerSize.width - ((colWidthsMap.get(coords.c.toString()) || CELL_WIDTH) * zoom);

                    if (!isVisible) {
                        containerRef.current.scrollTo({ top: Math.max(0, targetTop - 100), left: Math.max(0, targetLeft - 100), behavior: 'smooth' });
                    }
                }
            }
        }
    }, [activeCellId, rowToVisual, colToVisual, zoom, rowOffsets, colOffsets, containerSize, rowHeightsMap, colWidthsMap]);

    const handleScroll = (e: UIEvent<HTMLDivElement>) => {
        setScrollPosition({ top: e.currentTarget.scrollTop, left: e.currentTarget.scrollLeft });
        setContextMenu(null); 
    };

    const visibleRange = useMemo(() => {
        const targetTop = scrollPosition.top / zoom;
        let lowR = 0, highR = visibleRowIndices.length - 1;
        while (lowR <= highR) {
            const mid = (lowR + highR) >> 1;
            if (rowOffsets[mid] <= targetTop) lowR = mid + 1;
            else highR = mid - 1;
        }
        const startRowIndex = Math.max(0, highR);

        const targetBottom = targetTop + (containerSize.height / zoom);
        lowR = startRowIndex; highR = visibleRowIndices.length - 1;
        while (lowR <= highR) {
            const mid = (lowR + highR) >> 1;
            if (rowOffsets[mid] <= targetBottom) lowR = mid + 1;
            else highR = mid - 1;
        }
        const endRowIndex = Math.min(visibleRowIndices.length, lowR + 5);

        const targetLeft = scrollPosition.left / zoom;
        let lowC = 0, highC = visibleColIndices.length - 1;
        while (lowC <= highC) {
            const mid = (lowC + highC) >> 1;
            if (colOffsets[mid] <= targetLeft) lowC = mid + 1;
            else highC = mid - 1;
        }
        const startColIndex = Math.max(0, highC);

        const targetRight = targetLeft + (containerSize.width / zoom);
        lowC = startColIndex; highC = visibleColIndices.length - 1;
        while (lowC <= highC) {
            const mid = (lowC + highC) >> 1;
            if (colOffsets[mid] <= targetRight) lowC = mid + 1;
            else highC = mid - 1;
        }
        const endColIndex = Math.min(visibleColIndices.length, lowC + 2);

        const currentVisibleRows = Array.from(new Set([
            ...visibleRowIndices.slice(0, freezeRow),
            ...visibleRowIndices.slice(Math.max(freezeRow, startRowIndex), endRowIndex)
        ]));

        const currentVisibleCols = Array.from(new Set([
            ...visibleColIndices.slice(0, freezeCol),
            ...visibleColIndices.slice(Math.max(freezeCol, startColIndex), endColIndex)
        ]));

        return { currentVisibleRows, currentVisibleCols };
    }, [scrollPosition, containerSize, visibleRowIndices, visibleColIndices, zoom, freezeRow, freezeCol, rowOffsets, colOffsets]);

    const checkCondition = (value: any, condition: string) => {
        if (!condition) return false;
        const numValue = Number(value);
        const strValue = String(value).toUpperCase();
        const upperCond = condition.toUpperCase();

        if (condition.startsWith('>=')) return !isNaN(numValue) && numValue >= Number(condition.slice(2));
        if (condition.startsWith('<=')) return !isNaN(numValue) && numValue <= Number(condition.slice(2));
        if (condition.startsWith('>')) return !isNaN(numValue) && numValue > Number(condition.slice(1));
        if (condition.startsWith('<')) return !isNaN(numValue) && numValue < Number(condition.slice(1));
        if (condition.startsWith('=')) return strValue === condition.slice(1).toUpperCase();
        
        return strValue === upperCond;
    };

    const visibleCells: React.ReactElement[] = [];
    const colHeaders: React.ReactElement[] = [];
    const rowHeaders: React.ReactElement[] = [];

    visibleCells.push(
        <div key="corner"
            className="absolute bg-gray-200 border-b border-r border-gray-400 cursor-pointer hover:bg-gray-300 transition-colors"
            style={{ 
                width: HEADER_WIDTH, height: HEADER_HEIGHT, 
                top: scrollPosition.top / zoom, left: scrollPosition.left / zoom, 
                zIndex: 50 
            }}
            onClick={() => setSelection({ startR: 0, endR: totalRows - 1, startC: 0, endC: totalCols - 1 })}
            onContextMenu={(e) => {
                if (isReadOnly) return;
                e.preventDefault();
                setSelection({ startR: 0, endR: totalRows - 1, startC: 0, endC: totalCols - 1 });
                setContextMenu({ x: e.clientX, y: e.clientY, r: 0, c: 0 });
            }}
        />
    );

    const activeRules = conditionalRules.toArray().filter(r => r.sheetId === activeSheetId).map(rule => {
        let minR = 0, maxR = totalRows, minC = 0, maxC = totalCols;
        const targetRange = rule.range || rule.cellId; 
        
        if (targetRange && targetRange !== 'ALL') {
            if (targetRange.includes(':')) {
                const [start, end] = targetRange.split(':');
                const s = parseCellId(start);
                const e = parseCellId(end);
                minR = Math.min(s.r, e.r);
                maxR = Math.max(s.r, e.r);
                minC = Math.min(s.c, e.c);
                maxC = Math.max(s.c, e.c);
            } else {
                const c = parseCellId(targetRange);
                minR = c.r; maxR = c.r; minC = c.c; maxC = c.c;
            }
        }
        return { ...rule, minR, maxR, minC, maxC };
    });

    for (let i = 0; i < visibleRange.currentVisibleCols.length; i++) {
        const c = visibleRange.currentVisibleCols[i];
        const vCol = colToVisual.get(c)!;
        const isFrozenCol = vCol < freezeCol;
        const hasGroupLeft = c > 0 && groupedCols.has(c - 1);
        
        let colWidth = colWidthsMap.get(c.toString()) || CELL_WIDTH;
        if (tempSizes?.type === 'col' && tempSizes.index === c) colWidth = tempSizes.size;

        const leftPos = isFrozenCol 
            ? (scrollPosition.left / zoom) + colOffsets[vCol] + HEADER_WIDTH 
            : colOffsets[vCol] + HEADER_WIDTH;

        colHeaders.push(
            <div key={`ch-${c}`}
                className="absolute bg-gray-100 border-b border-r border-gray-300 flex items-center justify-center text-xs text-gray-600 font-medium select-none cursor-pointer hover:bg-gray-200 transition-colors"
                style={{ top: scrollPosition.top / zoom, left: leftPos, width: colWidth, height: HEADER_HEIGHT, zIndex: isFrozenCol ? 48 : 45 }}
                onClick={() => setSelection({ startR: 0, endR: totalRows - 1, startC: c, endC: c })}
                onContextMenu={(e) => {
                    if (isReadOnly) return;
                    e.preventDefault();
                    const inSelection = selection && c >= Math.min(selection.startC, selection.endC) && c <= Math.max(selection.startC, selection.endC);
                    if (!inSelection) {
                        setSelection({ startR: 0, endR: totalRows - 1, startC: c, endC: c });
                    }
                    setContextMenu({ x: e.clientX, y: e.clientY, r: 0, c });
                }}
            >
                {getColName(c)}
                {hasGroupLeft && (
                    <div 
                        className="group absolute top-0 left-0 h-full w-1 bg-blue-400 cursor-pointer flex items-center justify-center hover:w-4 hover:bg-blue-600 transition-all z-50 overflow-hidden"
                        onClick={(e) => { e.stopPropagation(); onUngroupCol(c); }}
                        title="Expand Group"
                    >
                        <svg className="opacity-0 group-hover:opacity-100 text-white" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                    </div>
                )}
                {!isReadOnly && (
                    <div 
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-500 z-50 transition-colors"
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            setResizing({ type: 'col', index: c, startPos: e.clientX, startSize: colWidth });
                        }}
                    />
                )}
            </div>
        );
    }

    for (let i = 0; i < visibleRange.currentVisibleRows.length; i++) {
        const r = visibleRange.currentVisibleRows[i];
        const vRow = rowToVisual.get(r)!;
        const isFrozenRow = vRow < freezeRow;
        const hasGroupAbove = r > 0 && groupedRows.has(r - 1);
        
        let rowHeight = rowHeightsMap.get(r.toString()) || CELL_HEIGHT;
        if (tempSizes?.type === 'row' && tempSizes.index === r) rowHeight = tempSizes.size;

        const topPos = isFrozenRow 
            ? (scrollPosition.top / zoom) + rowOffsets[vRow] + HEADER_HEIGHT 
            : rowOffsets[vRow] + HEADER_HEIGHT;

        rowHeaders.push(
            <div key={`rh-${r}`}
                className="absolute bg-gray-100 border-b border-r border-gray-300 flex items-center justify-center text-xs text-gray-600 font-medium select-none z-30 cursor-pointer hover:bg-gray-200 transition-colors"
                style={{ top: topPos, left: scrollPosition.left / zoom, width: HEADER_WIDTH, height: rowHeight, zIndex: isFrozenRow ? 48 : 45 }}
                onClick={() => setSelection({ startR: r, endR: r, startC: 0, endC: totalCols - 1 })}
                onContextMenu={(e) => {
                    if (isReadOnly) return;
                    e.preventDefault();
                    const inSelection = selection && r >= Math.min(selection.startR, selection.endR) && r <= Math.max(selection.startR, selection.endR);
                    if (!inSelection) {
                        setSelection({ startR: r, endR: r, startC: 0, endC: totalCols - 1 });
                    }
                    setContextMenu({ x: e.clientX, y: e.clientY, r, c: 0 });
                }}
            >
                {r + 1}
                {hasGroupAbove && (
                    <div 
                        className="group absolute top-0 left-0 w-full h-1 bg-blue-400 cursor-pointer flex items-center justify-center hover:h-4 hover:bg-blue-600 transition-all z-50 overflow-hidden"
                        onClick={(e) => { e.stopPropagation(); onUngroupRow(r); }}
                        title="Expand Group"
                    >
                        <svg className="opacity-0 group-hover:opacity-100 text-white" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                    </div>
                )}
                {!isReadOnly && (
                    <div 
                        className="absolute bottom-0 left-0 right-0 h-2 cursor-row-resize hover:bg-blue-500 z-50 transition-colors"
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            setResizing({ type: 'row', index: r, startPos: e.clientY, startSize: rowHeight });
                        }}
                    />
                )}
            </div>
        );

        for (let j = 0; j < visibleRange.currentVisibleCols.length; j++) {
            const c = visibleRange.currentVisibleCols[j];
            const vCol = colToVisual.get(c)!;
            const isFrozenCol = vCol < freezeCol;

            let colWidth = colWidthsMap.get(c.toString()) || CELL_WIDTH;
            if (tempSizes?.type === 'col' && tempSizes.index === c) colWidth = tempSizes.size;

            const leftPos = isFrozenCol 
                ? (scrollPosition.left / zoom) + colOffsets[vCol] + HEADER_WIDTH 
                : colOffsets[vCol] + HEADER_WIDTH;

            const cellId = `${getColName(c)}${r + 1}`;
            const rawValue = cellsMap.get(cellId) || '';
            const computedVal = computedValues[cellId] ?? rawValue;
            let displayValue = rawValue === '' ? '' : computedVal;
            
            let baseFormat: CellFormat = formatsMap.get(cellId) || {};
            let condFormat: CellFormat = {};
            
            if (displayValue !== '') {
                for (let ruleIndex = 0; ruleIndex < activeRules.length; ruleIndex++) {
                    const rule = activeRules[ruleIndex];
                    if (r >= rule.minR && r <= rule.maxR && c >= rule.minC && c <= rule.maxC) {
                        if (checkCondition(computedVal, rule.condition)) {
                            condFormat = { ...condFormat, ...rule.format };
                        }
                    }
                }
            }

            const activeFormat = { ...baseFormat, ...condFormat };

            if (activeFormat.numberFormat === 'currency' && !isNaN(Number(displayValue)) && displayValue !== '') {
                try {
                    displayValue = new Intl.NumberFormat('en-CA', { style: 'currency', currency: activeFormat.currencyCode || 'CAD' }).format(Number(displayValue));
                } catch (e) {
                    displayValue = `${activeFormat.currencyCode || 'CAD'} ${Number(displayValue).toFixed(2)}`;
                }
            } else if (activeFormat.numberFormat === 'percentage' && !isNaN(Number(displayValue)) && displayValue !== '') {
                displayValue = new Intl.NumberFormat('en-CA', { style: 'percent', maximumFractionDigits: 2 }).format(Number(displayValue));
            }

            const isSelected = selection && 
                r >= Math.min(selection.startR, selection.endR) && r <= Math.max(selection.startR, selection.endR) &&
                c >= Math.min(selection.startC, selection.endC) && c <= Math.max(selection.startC, selection.endC);
            
            const isFocused = activeCellId === cellId;
            const baseBg = activeFormat.bg || '#ffffff';
            const bgImage = (isSelected && !isFocused) ? 'linear-gradient(rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.15))' : 'none';

            let zIndex = 0;
            if (isFrozenRow && isFrozenCol) zIndex = 40;
            else if (isFrozenRow || isFrozenCol) zIndex = 35;
            
            if (isFocused) zIndex += 5; 
            else if (isSelected) zIndex += 2; 

            visibleCells.push(
                <input
                    key={cellId}
                    id={`cell-${cellId}`}
                    value={activeDomCell === cellId && !isReadOnly ? rawValue : displayValue}
                    readOnly={isReadOnly}
                    onChange={(e) => {
                        if (isReadOnly) return;
                        if (e.target.value === '') cellsMap.delete(cellId);
                        else cellsMap.set(cellId, e.target.value);
                    }}
                    onMouseDown={(e) => {
                        if (e.button === 2) {
                            const inSelection = selection && r >= Math.min(selection.startR, selection.endR) && r <= Math.max(selection.startR, selection.endR) && c >= Math.min(selection.startC, selection.endC) && c <= Math.max(selection.startC, selection.endC);
                            if (inSelection) {
                                e.preventDefault();
                            }
                        }
                    }}
                    onFocus={() => {
                        if (!isReadOnly) setActiveDomCell(cellId); 
                        onCellSelect(cellId);
                        setSelection({ startR: r, startC: c, endR: r, endC: c });
                    }}
                    onBlur={() => setActiveDomCell(null)}
                    onMouseEnter={(e) => {
                        if (e.buttons === 1) setSelection(prev => prev ? { ...prev, endR: r, endC: c } : null);
                    }}
                    onContextMenu={(e) => {
                        if (isReadOnly) return;
                        e.preventDefault();
                        const inSelection = selection && r >= Math.min(selection.startR, selection.endR) && r <= Math.max(selection.startR, selection.endR) && c >= Math.min(selection.startC, selection.endC) && c <= Math.max(selection.startC, selection.endC);
                        if (!inSelection) {
                            setSelection({ startR: r, startC: c, endR: r, endC: c });
                            onCellSelect(cellId);
                        }
                        setContextMenu({ x: e.clientX, y: e.clientY, r, c });
                    }}
                    onKeyDown={(e) => {
                        if (isReadOnly) return; 
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            e.currentTarget.blur();
                            
                            const offset = e.shiftKey ? -1 : 1;
                            const currentIdx = visibleRowIndices.indexOf(r);
                            const nextIdx = currentIdx + offset;
                            
                            if (nextIdx >= 0 && nextIdx < visibleRowIndices.length) {
                                const nextR = visibleRowIndices[nextIdx];
                                const nextCellId = `${getColName(c)}${nextR + 1}`;
                                
                                setTimeout(() => {
                                    onCellSelect(nextCellId);
                                    setSelection({ startR: nextR, startC: c, endR: nextR, endC: c });
                                    const nextEl = document.getElementById(`cell-${nextCellId}`);
                                    if (nextEl) nextEl.focus();
                                }, 0);
                            }
                        }
                    }}
                    className={`absolute border-r border-b border-gray-300 box-border px-1 text-sm outline-none transition-colors
                        ${isFocused ? 'ring-2 ring-green-500' : 'cursor-cell'}
                        ${activeDomCell === cellId && !isReadOnly ? 'cursor-text' : 'cursor-cell'}
                        ${isSelected && !isFocused ? 'ring-1 ring-blue-300' : ''}
                        ${isReadOnly ? 'bg-gray-50' : ''}`}
                    style={{
                        top: topPos, 
                        left: leftPos,   
                        width: colWidth,
                        height: rowHeight,
                        zIndex: zIndex,
                        fontWeight: activeFormat.bold ? 'bold' : 'normal',
                        fontStyle: activeFormat.italic ? 'italic' : 'normal',
                        fontFamily: activeFormat.fontFamily || 'inherit',
                        color: activeFormat.color || (rawValue.startsWith('=') && activeDomCell !== cellId ? '#2563eb' : '#111827'),
                        backgroundColor: baseBg,
                        backgroundImage: bgImage, 
                    }}
                />
            );
        }
    }

    const baseWidth = colOffsets[visibleColIndices.length] + HEADER_WIDTH;
    const baseHeight = rowOffsets[visibleRowIndices.length] + HEADER_HEIGHT;

    const selectedRowCount = selection && contextMenu ? Math.abs(selection.endR - selection.startR) + 1 : 1;
    const selectedColCount = selection && contextMenu ? Math.abs(selection.endC - selection.startC) + 1 : 1;

    return (
        <div 
            className="absolute inset-0 flex flex-col bg-gray-50 overflow-hidden"
            onClick={() => setContextMenu(null)}
        >
            {/* Global Resize Overlay */}
            {resizing && (
                <div 
                    className="fixed inset-0 z-[9999]" 
                    style={{ cursor: resizing.type === 'col' ? 'col-resize' : 'row-resize' }} 
                />
            )}

            <div 
                ref={containerRef}
                id="grid-scroll-container" 
                onScroll={handleScroll} 
                className="relative overflow-auto flex-grow custom-scrollbar bg-white"
            >
                <div style={{ width: baseWidth * zoom, height: baseHeight * zoom, position: 'relative' }}>
                    <div style={{ 
                        width: baseWidth, 
                        height: baseHeight, 
                        transform: `scale(${zoom})`, 
                        transformOrigin: '0 0',
                        position: 'absolute',
                        top: 0,
                        left: 0
                    }}>
                        {rowHeaders}
                        {colHeaders}
                        {visibleCells}
                    </div>
                </div>
            </div>

            {contextMenu && !isReadOnly && (
                <div 
                    className="fixed bg-white border border-gray-200 shadow-2xl rounded py-1 z-50 min-w-[160px] text-sm"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                >
                    <button className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors" 
                        onClick={() => { onInsertRow(Math.min(selection?.startR ?? contextMenu.r, selection?.endR ?? contextMenu.r), selectedRowCount); setContextMenu(null); }}>
                        Insert {selectedRowCount} row{selectedRowCount > 1 ? 's' : ''} above
                    </button>
                    <button className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors" 
                        onClick={() => { onInsertCol(Math.min(selection?.startC ?? contextMenu.c, selection?.endC ?? contextMenu.c), selectedColCount); setContextMenu(null); }}>
                        Insert {selectedColCount} column{selectedColCount > 1 ? 's' : ''} left
                    </button>
                    <div className="border-t border-gray-200 my-1"></div>
                    <button className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors" 
                        onClick={() => { onClearFormat(contextMenu.r, contextMenu.c); setContextMenu(null); }}>
                        Clear formatting
                    </button>
                </div>
            )}
        </div>
    );
};

export default VirtualisedGrid;